// Onboarding.jsx - Client onboarding applications (migration v5.2)
// Applications sent from the client portal's public /onboarding page. Review
// them here: approve, ask the client a question, reject, then mark setup
// complete once the client has sent their ViciDial access. Also manages the
// discount codes clients can enter on that page.
// Backend: routes/onboarding-admin.js (helpers/onboarding/).

import { useState, useEffect, useMemo, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ClipboardCheck, Search, RefreshCw, X, CheckCircle, AlertCircle, Loader2, Clock, MessageSquare, XCircle, Rocket,
  Trash2, Building2, Server, Bot, KeyRound, Eye, EyeOff, Copy, Check, ExternalLink, Tag, Plus, Flame
} from 'lucide-react'
import { onboardingAdminApi } from '../services/api'
import { useAuth } from '../context/AuthContext'
import { useNotifications } from '../context/NotificationContext'
import './Clients.css'

const STATUS = {
  PENDING: { label: 'Pending review', color: '#eab308', icon: Clock },
  CLARIFICATION_REQUESTED: { label: 'Awaiting client', color: '#818cf8', icon: MessageSquare },
  APPROVED: { label: 'Approved', color: '#22c55e', icon: CheckCircle },
  SETUP_COMPLETE: { label: 'Setup complete', color: '#38bdf8', icon: Rocket },
  REJECTED: { label: 'Rejected', color: '#ef4444', icon: XCircle }
}
const FILTERS = ['NEEDS_ACTION', 'ALL', ...Object.keys(STATUS)]
const PLAN_NAMES = { basic: 'Basic Plan', business: 'Business Plan', business_plus: 'Business Plus' }
const REJECTION_REASONS = [
  'Dialer is not ViciDial or cannot be integrated',
  'Compliance concerns with the campaign',
  'Incomplete or unverifiable company details',
  'Duplicate application',
  'Other'
]
const REVIEW_PROMISE_MINUTES = 120 // the onboarding page promises a review within 2 hours

// Waiting on the team: new/replied applications, and approved ones whose ViciDial access has arrived
const needsAction = (a) => a.status === 'PENDING' || (a.status === 'APPROVED' && a.technicalSubmitted)

const formatDate = (value) =>
  value ? new Date(value).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—'

function waitingFor(a) {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(a.updatedAt || a.createdAt).getTime()) / 60000))
  const text = minutes < 60 ? `${minutes}m` : minutes < 1440 ? `${Math.floor(minutes / 60)}h ${minutes % 60}m` : `${Math.floor(minutes / 1440)}d`
  return { text, late: minutes > REVIEW_PROMISE_MINUTES }
}

const describeDiscount = (d) =>
  d.discountType === 'percentage' ? `${d.value}% off the monthly fee` : `$${d.value} off per bot`

const errorText = (err, fallback) => err.response?.data?.error || fallback

function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.PENDING
  return (
    <span className="status-badge-sm tw-inline-flex tw-items-center tw-gap-1" style={{ background: `${s.color}20`, color: s.color }}>
      <s.icon size={11} /> {s.label}
    </span>
  )
}

function CopyButton({ value, label }) {
  const [copied, setCopied] = useState(false)
  return (
    <button type="button" className="btn btn-icon btn-sm" title={label} aria-label={label}
      onClick={() => { navigator.clipboard?.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500) }}>
      {copied ? <Check size={12} className="tw-text-success" /> : <Copy size={12} />}
    </button>
  )
}

function Row({ label, children, mono }) {
  return (
    <div className="tw-flex tw-items-start tw-justify-between tw-gap-4 tw-border-0 tw-border-b tw-border-solid tw-border-border tw-py-1.5 tw-text-sm last:tw-border-b-0">
      <span className="tw-flex-shrink-0 tw-text-muted">{label}</span>
      <span className={`tw-min-w-0 tw-break-words tw-text-right ${mono ? 'tw-font-mono tw-text-xs' : ''}`}>{children}</span>
    </div>
  )
}

function Section({ icon: Icon, title, children }) {
  return (
    <section className="tw-rounded-md tw-border tw-border-solid tw-border-border tw-p-3">
      <h3 className="tw-mb-1 tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-muted">
        <Icon size={13} /> {title}
      </h3>
      {children}
    </section>
  )
}

function Modal({ title, subtitle, onClose, wide, children, footer }) {
  return (
    <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
      <motion.div className="modal" style={wide ? { maxWidth: 900 } : undefined}
        initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }}
        onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="tw-min-w-0">
            {subtitle && <span className="tw-font-mono tw-text-xs tw-text-muted">{subtitle}</span>}
            <h2>{title}</h2>
          </div>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </motion.div>
    </motion.div>
  )
}

// --- Application details ----------------------------------------------------------

function ApplicationModal({ appId, onClose, onChanged, showToast }) {
  const { user, isAdmin } = useAuth()
  const [app, setApp] = useState(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState('')
  const [dialog, setDialog] = useState(null) // null | 'clarify' | 'reject' | 'delete'
  const [message, setMessage] = useState('')
  const [reason, setReason] = useState(REJECTION_REASONS[0])
  const [password, setPassword] = useState(null)
  const username = user?.username || user?.name || 'Dashboard'

  const load = useCallback(async () => {
    try {
      const res = await onboardingAdminApi.getApplication(appId)
      setApp(res.data.application)
    } catch (err) {
      setError(errorText(err, 'Failed to load the application'))
    }
  }, [appId])
  useEffect(() => { load() }, [load])

  const act = async (action, text) => {
    setError(''); setBusy(action)
    try {
      const res = await onboardingAdminApi.act(appId, action, text, username)
      setApp(res.data.application)
      setDialog(null); setMessage('')
      showToast(`Application ${STATUS[res.data.application.status].label.toLowerCase()}`)
      onChanged()
    } catch (err) {
      setError(errorText(err, 'Action failed'))
    } finally {
      setBusy('')
    }
  }

  const reveal = async () => {
    setBusy('reveal')
    try {
      const res = await onboardingAdminApi.revealPassword(appId, username)
      setPassword(res.data.level9Password)
      load() // the reveal is in the audit log
    } catch (err) {
      setError(errorText(err, 'Could not reveal the password'))
    } finally {
      setBusy('')
    }
  }

  const remove = async () => {
    setBusy('delete')
    try {
      await onboardingAdminApi.deleteApplication(appId, username)
      showToast('Application data deleted')
      onChanged()
      onClose()
    } catch (err) {
      setError(errorText(err, 'Delete failed'))
      setBusy('')
    }
  }

  const d = app?.data
  const status = app?.status
  const t = app?.technical

  return (
    <Modal wide onClose={onClose} subtitle={appId}
      title={<span className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">{app?.officeName || 'Loading…'} {status && <StatusBadge status={status} />}</span>}>
      {error && <div className="tw-mb-3 tw-rounded-md tw-bg-red-500/10 tw-px-3 tw-py-2 tw-text-sm tw-text-red-300">{error}</div>}
      {!app ? (!error && <div className="tw-flex tw-justify-center tw-py-12"><Loader2 size={28} className="spin" /></div>) : (
        <div className="tw-flex tw-flex-col tw-gap-3">
          {/* Actions */}
          <div className="tw-flex tw-flex-wrap tw-gap-2">
            {['PENDING', 'CLARIFICATION_REQUESTED'].includes(status) && (
              <button className="btn btn-success btn-sm" disabled={!!busy} onClick={() => act('approve')}>
                {busy === 'approve' ? <Loader2 size={14} className="spin" /> : <CheckCircle size={14} />} Approve
              </button>
            )}
            {['PENDING', 'APPROVED'].includes(status) && (
              <button className="btn btn-secondary btn-sm" disabled={!!busy} onClick={() => { setDialog('clarify'); setMessage('') }}>
                <MessageSquare size={14} /> Ask the client
              </button>
            )}
            {status === 'APPROVED' && (
              <button className="btn btn-primary btn-sm" disabled={!!busy || !app.technicalSubmittedAt} onClick={() => act('complete')}
                title={app.technicalSubmittedAt ? '' : 'Waiting for the client to send their ViciDial access'}>
                {busy === 'complete' ? <Loader2 size={14} className="spin" /> : <Rocket size={14} />} Mark setup complete
              </button>
            )}
            {['PENDING', 'CLARIFICATION_REQUESTED', 'APPROVED'].includes(status) && (
              <button className="btn btn-secondary btn-sm" disabled={!!busy} onClick={() => { setDialog('reject'); setMessage(''); setReason(REJECTION_REASONS[0]) }}>
                <XCircle size={14} className="tw-text-red-400" /> Reject
              </button>
            )}
            {isAdmin && (
              <button className="btn btn-ghost btn-sm tw-ml-auto" disabled={!!busy} onClick={() => setDialog('delete')}>
                <Trash2 size={14} className="tw-text-red-400" /> Delete data
              </button>
            )}
          </div>

          {/* Clarify / reject / delete */}
          {dialog === 'clarify' && (
            <div className="form-group tw-rounded-md tw-border tw-border-solid tw-border-border tw-p-3">
              <label>Message to the client</label>
              <textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} autoFocus
                placeholder="e.g. Please confirm which VoIP carrier you use." />
              <p className="tw-mt-1 tw-text-xs tw-text-muted">The client sees this on their application page and can reply there.</p>
              <div className="tw-mt-2 tw-flex tw-justify-end tw-gap-2">
                <button className="btn btn-secondary btn-sm" onClick={() => setDialog(null)}>Cancel</button>
                <button className="btn btn-primary btn-sm" disabled={!message.trim() || !!busy} onClick={() => act('clarify', message)}>
                  {busy === 'clarify' && <Loader2 size={14} className="spin" />} Send to client
                </button>
              </div>
            </div>
          )}
          {dialog === 'reject' && (
            <div className="form-group tw-rounded-md tw-border tw-border-solid tw-border-red-500/40 tw-p-3">
              <label>Reason (shown to the client)</label>
              <select value={reason} onChange={(e) => setReason(e.target.value)}>
                {REJECTION_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <textarea className="tw-mt-2" rows={2} value={message} onChange={(e) => setMessage(e.target.value)}
                placeholder={reason === 'Other' ? 'Explain the reason' : 'Optional details'} />
              <div className="tw-mt-2 tw-flex tw-justify-end tw-gap-2">
                <button className="btn btn-secondary btn-sm" onClick={() => setDialog(null)}>Cancel</button>
                <button className="btn btn-danger btn-sm" disabled={(reason === 'Other' && !message.trim()) || !!busy}
                  onClick={() => act('reject', reason === 'Other' ? message : [reason, message.trim()].filter(Boolean).join('. '))}>
                  {busy === 'reject' && <Loader2 size={14} className="spin" />} Reject application
                </button>
              </div>
            </div>
          )}
          {dialog === 'delete' && (
            <div className="tw-rounded-md tw-border tw-border-solid tw-border-red-500/40 tw-bg-red-500/5 tw-p-3 tw-text-sm">
              This permanently deletes all of <strong>{app.officeName}</strong>'s data, including their ViciDial credentials. Use it when
              their contract ends. It cannot be undone.
              <div className="tw-mt-2 tw-flex tw-justify-end tw-gap-2">
                <button className="btn btn-secondary btn-sm" onClick={() => setDialog(null)}>Cancel</button>
                <button className="btn btn-danger btn-sm" disabled={busy === 'delete'} onClick={remove}>
                  {busy === 'delete' && <Loader2 size={14} className="spin" />} Delete permanently
                </button>
              </div>
            </div>
          )}

          {app.statusMessage && (
            <div className="tw-rounded-md tw-bg-elevated tw-px-3 tw-py-2 tw-text-sm">
              <span className="tw-block tw-text-xs tw-text-muted">Latest message to the client</span>
              <span className="tw-whitespace-pre-wrap">{app.statusMessage}</span>
            </div>
          )}

          <div className="tw-grid tw-grid-cols-1 tw-gap-3 md:tw-grid-cols-2">
            <Section icon={Building2} title="Client">
              <Row label="Company">{d.officeName}</Row>
              <Row label="Contact">{d.pocName}, {d.pocDesignation}</Row>
              <Row label="Email"><a href={`mailto:${d.email}`}>{d.email}</a></Row>
              <Row label="Address">{d.officeAddress}</Row>
              <Row label={d.contactMethod === 'teams' ? 'Teams' : 'WhatsApp'} mono>
                {d.contactMethod === 'whatsapp' && d.whatsappE164 ? (
                  <a href={`https://wa.me/${d.whatsappE164.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="tw-inline-flex tw-items-center tw-gap-1">
                    {app.contactEndpoint} <ExternalLink size={11} />
                  </a>
                ) : app.contactEndpoint}
              </Row>
            </Section>
            <Section icon={Server} title="ViciDial">
              <Row label="Vendor">{d.serverVendor}</Row>
              <Row label="Link" mono>{d.dialerLink}</Row>
              <Row label="Campaign">{d.campaignLabel}</Row>
              <Row label="Function">{d.botObjective}</Row>
              {d.additionalNotes && <Row label="Notes">{d.additionalNotes}</Row>}
            </Section>
          </div>

          <Section icon={Bot} title={PLAN_NAMES[app.planId] || app.planId}>
            <div className="tw-flex tw-flex-wrap tw-items-end tw-justify-between tw-gap-3">
              <div>
                <span className="tw-text-2xl tw-font-bold">${app.monthlyTotal.toLocaleString()}</span>
                <span className="tw-text-sm tw-text-muted"> / month</span>
                <p className="tw-text-sm tw-text-muted">
                  {app.bots} bots × ${app.unitPrice}
                  {app.discountCode && <span className="tw-text-success"> · {app.discountCode}</span>}
                </p>
              </div>
              <div className="tw-text-right tw-text-xs tw-text-muted">
                {app.humanAgents && <p>{app.humanAgents} human agents · {app.transferMode === 'quantity' ? 'Quantity' : 'Quality'} transfers</p>}
                {d.recommendation && <p>Recommended: {d.recommendation.bots} bots{d.recommendation.enterprise ? ' (needs enterprise plan)' : ''}</p>}
                <p>Terms accepted {formatDate(d.termsAcceptedAt)}</p>
              </div>
            </div>
          </Section>

          <Section icon={KeyRound} title="ViciDial access">
            {t ? (
              <>
                <Row label="Submitted">{formatDate(app.technicalSubmittedAt)}</Row>
                <Row label="Admin link" mono>{t.adminLink}</Row>
                <Row label="IP whitelisting" mono>{t.ipWhitelistLink}</Row>
                <Row label="Level 9 user" mono>
                  <span className="tw-inline-flex tw-items-center tw-gap-1">{t.level9Username} <CopyButton value={t.level9Username} label="Copy username" /></span>
                </Row>
                <Row label="Level 9 password" mono>
                  {password ? (
                    <span className="tw-inline-flex tw-items-center tw-gap-1">
                      {password}
                      <CopyButton value={password} label="Copy password" />
                      <button type="button" className="btn btn-icon btn-sm" title="Hide" aria-label="Hide password" onClick={() => setPassword(null)}><EyeOff size={12} /></button>
                    </span>
                  ) : (
                    <button type="button" className="btn btn-ghost btn-sm" disabled={busy === 'reveal'} onClick={reveal}>
                      {busy === 'reveal' ? <Loader2 size={12} className="spin" /> : <Eye size={12} />} Reveal (logged)
                    </button>
                  )}
                </Row>
                <Row label="Fronting campaign" mono>{t.frontingCampaignName}</Row>
                {t.verificationDialerLink && <Row label="Verification dialer" mono>{t.verificationDialerLink}</Row>}
                {t.verificationTransferQueue && <Row label="Transfer queue">{t.verificationTransferQueue}</Row>}
                {t.additionalTechNotes && <Row label="Notes">{t.additionalTechNotes}</Row>}
              </>
            ) : (
              <p className="tw-py-1 tw-text-sm tw-text-muted">
                {status === 'APPROVED' ? 'Waiting for the client to send their ViciDial access.' : 'Requested from the client after approval.'}
              </p>
            )}
          </Section>

          <Section icon={Clock} title="Activity">
            <ol className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
              {app.auditLog.map((entry, i) => (
                <li key={i} className="tw-flex tw-gap-3 tw-text-sm">
                  <span className="tw-w-36 tw-flex-shrink-0 tw-font-mono tw-text-xs tw-text-muted">{formatDate(entry.at)}</span>
                  <span className="tw-min-w-0">
                    <strong>{entry.actor}</strong>: {entry.action}
                    {entry.detail && <span className="tw-block tw-whitespace-pre-wrap tw-text-muted">{entry.detail}</span>}
                  </span>
                </li>
              ))}
            </ol>
          </Section>
        </div>
      )}
    </Modal>
  )
}

// --- Applications list ------------------------------------------------------------

function ApplicationsTab({ showToast }) {
  const { refreshNotifications } = useNotifications()
  const [applications, setApplications] = useState(null)
  const [counts, setCounts] = useState({})
  const [loadError, setLoadError] = useState('')
  const [refreshing, setRefreshing] = useState(false)
  const [filter, setFilter] = useState('NEEDS_ACTION')
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState(null)

  const load = useCallback(async (showSpinner = false) => {
    if (showSpinner) setRefreshing(true)
    try {
      const res = await onboardingAdminApi.getApplications()
      setApplications(res.data.applications || [])
      setCounts(res.data.counts || {})
      setLoadError('')
      refreshNotifications()
    } catch (err) {
      setLoadError(errorText(err, 'Failed to load applications'))
    } finally {
      setRefreshing(false)
    }
  }, [refreshNotifications])

  // New submissions show up without a reload (silent refresh every 30 seconds)
  useEffect(() => {
    load()
    const id = setInterval(() => load(), 30000)
    return () => clearInterval(id)
  }, [load])

  const actionCount = (applications || []).filter(needsAction).length
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    return (applications || []).filter((a) =>
      (filter === 'ALL' || (filter === 'NEEDS_ACTION' ? needsAction(a) : a.status === filter)) &&
      (!q || [a.appId, a.officeName, a.pocName, a.email, a.campaignLabel, a.serverVendor].some((v) => (v || '').toLowerCase().includes(q))))
  }, [applications, filter, search])

  const filterLabel = (f) =>
    f === 'ALL' ? 'All' : f === 'NEEDS_ACTION' ? `Needs action (${actionCount})` : `${STATUS[f].label}${counts[f] ? ` (${counts[f]})` : ''}`

  return (
    <>
      <div className="tw-mb-3 tw-flex tw-flex-wrap tw-items-center tw-gap-2">
        <div className="tw-flex tw-min-w-[240px] tw-max-w-sm tw-flex-1 tw-items-center tw-gap-2 tw-rounded-md tw-border tw-border-solid tw-border-border tw-bg-input tw-px-3">
          <Search size={16} className="tw-text-muted" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search company, contact, email, campaign or reference…"
            className="tw-w-full tw-border-0 tw-bg-transparent tw-py-2 tw-outline-none" />
        </div>
        <div className="tw-flex tw-flex-wrap tw-gap-1.5">
          {FILTERS.map((f) => (
            <button key={f} type="button" onClick={() => setFilter(f)}
              className={`btn btn-sm ${filter === f ? 'btn-primary' : 'btn-secondary'}`}>{filterLabel(f)}</button>
          ))}
        </div>
        <button className="btn btn-secondary btn-sm tw-ml-auto" onClick={() => load(true)} disabled={refreshing}>
          <RefreshCw size={14} className={refreshing ? 'spin' : ''} /> Refresh
        </button>
      </div>

      {loadError && <div className="tw-mb-3 tw-rounded-md tw-bg-red-500/10 tw-px-3 tw-py-2 tw-text-sm tw-text-red-300">{loadError}</div>}

      <div className="table-container clients-table-compact">
        <table className="data-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Contact</th>
              <th>Campaign &amp; vendor</th>
              <th style={{ width: '170px' }}>Plan</th>
              <th style={{ width: '150px' }}>Submitted</th>
              <th style={{ width: '170px' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {!applications ? (
              <tr><td colSpan="6" className="empty-state">{loadError ? <p>Could not load applications</p> : <Loader2 size={28} className="spin" />}</td></tr>
            ) : visible.length === 0 ? (
              <tr>
                <td colSpan="6" className="empty-state">
                  <ClipboardCheck size={48} />
                  <p>{applications.length ? 'No applications match these filters' : 'No applications yet. New submissions from /onboarding appear here automatically.'}</p>
                </td>
              </tr>
            ) : visible.map((a) => {
              const waiting = needsAction(a) && waitingFor(a)
              return (
                <tr key={a.appId} onClick={() => setSelected(a.appId)} style={{ cursor: 'pointer' }}>
                  <td>
                    <div className="client-name-col">
                      <span className="client-name">{a.officeName}</span>
                      <span className="client-contact-sub tw-font-mono">{a.appId}</span>
                    </div>
                  </td>
                  <td>
                    <div className="client-name-col">
                      <span>{a.pocName}</span>
                      <span className="client-contact-sub tw-font-mono">{a.contactEndpoint}</span>
                    </div>
                  </td>
                  <td>
                    <div className="client-name-col">
                      <span>{a.campaignLabel}</span>
                      <span className="client-contact-sub">{a.serverVendor}</span>
                    </div>
                  </td>
                  <td>
                    <div className="client-name-col">
                      <span className="tw-font-semibold">${a.monthlyTotal.toLocaleString()}/mo</span>
                      <span className="client-contact-sub">{a.bots} bots × ${a.unitPrice}{a.discountCode ? ` · ${a.discountCode}` : ''}</span>
                    </div>
                  </td>
                  <td className="tw-text-sm tw-text-muted">{formatDate(a.createdAt)}</td>
                  <td>
                    <StatusBadge status={a.status} />
                    {waiting && (
                      <span className={`tw-mt-1 tw-flex tw-items-center tw-gap-1 tw-text-xs ${waiting.late ? 'tw-text-red-400' : 'tw-text-muted'}`}>
                        {waiting.late && <Flame size={11} />} waiting {waiting.text}
                      </span>
                    )}
                    {a.status === 'APPROVED' && (
                      <span className="tw-mt-1 tw-block tw-text-xs tw-text-muted">{a.technicalSubmitted ? 'Access received' : 'Awaiting access'}</span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <AnimatePresence>
        {selected && (
          <ApplicationModal key={selected} appId={selected} onClose={() => setSelected(null)} onChanged={() => load()} showToast={showToast} />
        )}
      </AnimatePresence>
    </>
  )
}

// --- Discount codes ---------------------------------------------------------------

const EMPTY_DISCOUNT = { code: '', discountType: 'percentage', value: '10', maxUses: '1', assignedTo: '', reason: '' }

function DiscountsTab({ showToast }) {
  const { user } = useAuth()
  const username = user?.username || user?.name || 'Dashboard'
  const [discounts, setDiscounts] = useState(null)
  const [error, setError] = useState('')
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState(EMPTY_DISCOUNT)
  const [fieldErrors, setFieldErrors] = useState({})
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    try {
      const res = await onboardingAdminApi.getDiscounts()
      setDiscounts(res.data.discounts || [])
    } catch (err) {
      setError(errorText(err, 'Failed to load discount codes'))
    }
  }, [])
  useEffect(() => { load() }, [load])

  const create = async (e) => {
    e.preventDefault()
    setSaving(true); setFieldErrors({})
    try {
      await onboardingAdminApi.createDiscount({ ...form, value: Number(form.value) }, username)
      showToast(`Discount code ${form.code.toUpperCase()} created`)
      setCreating(false); setForm(EMPTY_DISCOUNT)
      load()
    } catch (err) {
      setFieldErrors(err.response?.data?.fields || { form: errorText(err, 'Failed to create the code') })
    } finally {
      setSaving(false)
    }
  }

  const toggle = async (d) => {
    try {
      await onboardingAdminApi.setDiscountActive(d.code, !d.active, username)
      showToast(`${d.code} ${d.active ? 'disabled' : 'enabled'}`)
      load()
    } catch (err) {
      showToast(errorText(err, 'Failed to update the code'), 'error')
    }
  }

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))
  const fieldError = (k) => fieldErrors[k] && <span className="tw-mt-1 tw-block tw-text-xs tw-text-red-400">{fieldErrors[k]}</span>

  return (
    <>
      <div className="tw-mb-3 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
        <p className="tw-m-0 tw-text-sm tw-text-muted">Clients enter these on /onboarding. The server checks and counts each use, so usage limits always hold.</p>
        <button className="btn btn-primary" onClick={() => { setForm(EMPTY_DISCOUNT); setFieldErrors({}); setCreating(true) }}>
          <Plus size={16} /> New discount code
        </button>
      </div>

      {error && <div className="tw-mb-3 tw-rounded-md tw-bg-red-500/10 tw-px-3 tw-py-2 tw-text-sm tw-text-red-300">{error}</div>}

      <div className="table-container clients-table-compact">
        <table className="data-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Discount</th>
              <th>For</th>
              <th style={{ width: '90px', textAlign: 'center' }}>Uses</th>
              <th>Approved by</th>
              <th style={{ width: '100px', textAlign: 'center' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {!discounts ? (
              <tr><td colSpan="6" className="empty-state">{error ? <p>Could not load discount codes</p> : <Loader2 size={28} className="spin" />}</td></tr>
            ) : discounts.length === 0 ? (
              <tr><td colSpan="6" className="empty-state"><Tag size={48} /><p>No discount codes yet</p></td></tr>
            ) : discounts.map((d) => (
              <tr key={d.code}>
                <td><span className="tw-inline-flex tw-items-center tw-gap-1 tw-font-mono tw-font-semibold">{d.code} <CopyButton value={d.code} label={`Copy ${d.code}`} /></span></td>
                <td className="tw-text-success">{describeDiscount(d)}</td>
                <td>
                  <div className="client-name-col">
                    <span>{d.assignedTo || 'Any client'}</span>
                    <span className="client-contact-sub">{d.reason}</span>
                  </div>
                </td>
                <td style={{ textAlign: 'center' }} className="tw-font-mono">{d.usedCount} / {d.maxUses ?? '∞'}</td>
                <td>
                  <div className="client-name-col">
                    <span>{d.createdBy}</span>
                    <span className="client-contact-sub">{formatDate(d.createdAt)}</span>
                  </div>
                </td>
                <td style={{ textAlign: 'center' }}>
                  <button type="button" className={`status-badge-sm ${d.active ? 'status-active' : ''}`} style={{ cursor: 'pointer', border: 0 }}
                    title={d.active ? 'Click to disable' : 'Click to enable'} onClick={() => toggle(d)}>
                    {d.active ? 'Active' : 'Disabled'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AnimatePresence>
        {creating && (
          <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setCreating(false)}>
            <motion.div className="modal" initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>New discount code</h2>
                <button className="modal-close" onClick={() => setCreating(false)}><X size={20} /></button>
              </div>
              <form onSubmit={create}>
                <div className="modal-body">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Code *</label>
                      <input value={form.code} onChange={set('code')} required placeholder="e.g. APEX-PILOT" className="tw-font-mono tw-uppercase" />
                      {fieldError('code')}
                    </div>
                    <div className="form-group">
                      <label>Assigned to</label>
                      <input value={form.assignedTo} onChange={set('assignedTo')} placeholder="Client name (optional)" />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Type</label>
                      <select value={form.discountType} onChange={set('discountType')}>
                        <option value="percentage">% off the monthly fee</option>
                        <option value="per_bot">$ off per bot</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>{form.discountType === 'percentage' ? 'Percent (1–50) *' : 'Dollars per bot (1–15) *'}</label>
                      <input type="number" min="1" value={form.value} onChange={set('value')} required />
                      {fieldError('value')}
                    </div>
                    <div className="form-group">
                      <label>Max uses</label>
                      <input type="number" min="1" value={form.maxUses} onChange={set('maxUses')} placeholder="Unlimited" />
                      {fieldError('maxUses')}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Reason *</label>
                    <textarea rows={2} value={form.reason} onChange={set('reason')} required placeholder="Why this discount was approved" />
                    {fieldError('reason')}
                  </div>
                  {fieldErrors.form && <div className="tw-rounded-md tw-bg-red-500/10 tw-px-3 tw-py-2 tw-text-sm tw-text-red-300">{fieldErrors.form}</div>}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setCreating(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>{saving && <Loader2 size={14} className="spin" />} Create code</button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

// --- Page -------------------------------------------------------------------------

export default function Onboarding() {
  const [tab, setTab] = useState('applications')
  const [toast, setToast] = useState(null)

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 4000)
  }, [])

  return (
    <div className="clients-page">
      <AnimatePresence>
        {toast && (
          <motion.div className={`toast ${toast.type}`}
            initial={{ opacity: 0, y: -20, x: '-50%' }} animate={{ opacity: 1, y: 0, x: '-50%' }} exit={{ opacity: 0, y: -20, x: '-50%' }}>
            {toast.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">Onboarding</h1>
          <p className="page-subtitle">Applications from the client portal's /onboarding page. Clients are promised a review within 2 hours.</p>
        </div>
      </div>

      <div className="tw-mb-4 tw-flex tw-gap-1 tw-border-0 tw-border-b tw-border-solid tw-border-border">
        {[
          { id: 'applications', label: 'Applications', icon: ClipboardCheck },
          { id: 'discounts', label: 'Discount codes', icon: Tag }
        ].map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
            className={`tw-flex tw-cursor-pointer tw-items-center tw-gap-1.5 tw-border-0 tw-border-b-2 tw-border-solid tw-bg-transparent tw-px-4 tw-py-2 tw-text-sm tw-font-medium ${
              tab === t.id ? 'tw-border-primary tw-text-fg' : 'tw-border-transparent tw-text-muted'}`}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'applications' ? <ApplicationsTab showToast={showToast} /> : <DiscountsTab showToast={showToast} />}
    </div>
  )
}
